import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, ArrowLeftRight, Save, Trash2, AlertTriangle } from 'lucide-react';

interface Insurer { id: string; name: string; fsp_number: string; contact_email: string; city: string; active: boolean; created_at?: string; updated_at?: string }

export const MasterInsurers: React.FC = () => {
  const [rows, setRows] = useState<Insurer[]>([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', contact_first_name: '', contact_last_name: '', contact_email: '', contact_mobile: '', support_phone: '', fsp_number: '', company_reg_number: '', vat_number: '', address_street: '', city: '', province: '', postal_code: '', country: 'South Africa' });
  const [createdAdmin, setCreatedAdmin] = useState<{ email: string; full_name: string; temp_password: string; insurer: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState<Insurer | null>(null);
  const [del, setDel] = useState<Insurer | null>(null);
  const [delConfirm, setDelConfirm] = useState('');
  const { switchIntoInsurer } = useAuth();
  const nav = useNavigate();

  const load = async (): Promise<void> => { const r = await api.get<Insurer[]>('/master/insurers'); setRows(r.data); };
  useEffect(() => { load(); }, []);

  const create = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Insurer name is required.'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ name: string; first_admin: { email: string; full_name: string; temp_password: string } }>('/master/insurers', form);
      toast.success('Insurer created. First Super Admin login generated.');
      setCreating(false);
      setCreatedAdmin({ ...r.data.first_admin, insurer: r.data.name });
      setForm({ name: '', contact_first_name: '', contact_last_name: '', contact_email: '', contact_mobile: '', support_phone: '', fsp_number: '', company_reg_number: '', vat_number: '', address_street: '', city: '', province: '', postal_code: '', country: 'South Africa' });
      await load();
    }
    catch (err: any) { toast.error(err?.response?.data?.error || 'Create failed.'); }
    finally { setBusy(false); }
  };

  const saveEdit = async (): Promise<void> => {
    if (!edit) return;
    setBusy(true);
    try { await api.put(`/master/insurers/${edit.id}`, edit); toast.success('Insurer updated.'); setEdit(null); await load(); }
    catch (err: any) { toast.error(err?.response?.data?.error || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const doDelete = async (): Promise<void> => {
    if (!del || delConfirm !== 'DELETE') return;
    setBusy(true);
    try {
      const r = await api.delete<{ records_deleted: number }>(`/master/insurers/${del.id}`, { data: { confirm: 'DELETE' } });
      toast.success(`${del.name} deleted — ${r.data.records_deleted} record(s) removed.`);
      setDel(null); setDelConfirm('');
      await load();
    } catch (err: any) { toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Delete failed.'); }
    finally { setBusy(false); }
  };

  const doSwitch = async (id: string): Promise<void> => {
    if (!confirm(`Switch into ${id}? You will be signed in as the insurer's Super Admin. The action is recorded in the audit trail.`)) return;
    try { await switchIntoInsurer(id); toast.success(`Now operating as ${id}`); nav('/insurer', { replace: true }); }
    catch (err: any) { toast.error(err?.response?.data?.error || 'Switch failed.'); }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="master-insurers">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Insurers</h2>
          <p className="text-sm text-raptor-navy/60 font-body">Every underwriter tenant on the platform. Add new insurers here, adjust their profile, or switch into their portal to help with operational tasks.</p>
        </div>
        <Button variant="gradient" onClick={() => setCreating(!creating)} data-testid="new-insurer-btn"><Plus size={16} /> {creating ? 'Cancel' : 'New insurer'}</Button>
      </div>

      {createdAdmin && (
        <Card className="border-emerald-300 bg-emerald-50/60" data-testid="first-admin-panel">
          <CardContent className="p-4 space-y-2">
            <div className="font-headline text-lg text-emerald-800">First Super Admin created for {createdAdmin.insurer}</div>
            <div className="text-sm text-raptor-navy/80">Share these one-time credentials with <b>{createdAdmin.full_name}</b>. They'll be forced to set a new password on first login. This password is shown <b>only now</b>.</div>
            <div className="grid sm:grid-cols-2 gap-3 pt-1">
              <div className="rounded-md bg-white border border-emerald-200 p-2.5"><div className="text-[11px] uppercase text-raptor-navy/50">Email</div><div className="font-tabular text-sm" data-testid="first-admin-email">{createdAdmin.email}</div></div>
              <div className="rounded-md bg-white border border-emerald-200 p-2.5"><div className="text-[11px] uppercase text-raptor-navy/50">Temporary password</div><div className="font-tabular text-sm" data-testid="first-admin-password">{createdAdmin.temp_password}</div></div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(`Email: ${createdAdmin.email}\nTemporary password: ${createdAdmin.temp_password}`); toast.success('Credentials copied.'); }}>Copy credentials</Button>
              <Button size="sm" variant="ghost" onClick={() => setCreatedAdmin(null)} data-testid="dismiss-first-admin">Dismiss</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {creating && (
        <Card>
          <CardHeader><CardTitle>New insurer</CardTitle><CardDescription>These become the insurer's organisation identity &amp; compliance details (their Settings → General). Start typing a street address to auto-fill city, province, postal code and country.</CardDescription></CardHeader>
          <CardContent>
            <form onSubmit={create} className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2 text-xs font-semibold uppercase tracking-wide text-raptor-navy/50">Primary contact — becomes the first Super Admin for this tenant</div>
              <div className="space-y-1.5"><Label>First name *</Label><Input required value={form.contact_first_name} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} data-testid="insurer-form-firstname" /></div>
              <div className="space-y-1.5"><Label>Surname *</Label><Input required value={form.contact_last_name} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })} data-testid="insurer-form-lastname" /></div>
              <div className="space-y-1.5"><Label>Email *</Label><Input required type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="insurer-form-email" /></div>
              <div className="space-y-1.5"><Label>Mobile phone *</Label><Input required value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} placeholder="+27…" data-testid="insurer-form-mobile" /></div>
              <div className="md:col-span-2 border-t border-border/50 pt-3 text-xs font-semibold uppercase tracking-wide text-raptor-navy/50">Organisation details</div>
              <div className="space-y-1.5"><Label>Organisation name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="insurer-form-name" /></div>
              <div className="space-y-1.5"><Label>Support phone</Label><Input value={form.support_phone} onChange={(e) => setForm({ ...form, support_phone: e.target.value })} data-testid="insurer-form-phone" /></div>
              <div className="space-y-1.5"><Label>FSP number</Label><Input value={form.fsp_number} onChange={(e) => setForm({ ...form, fsp_number: e.target.value })} data-testid="insurer-form-fsp" /></div>
              <div className="space-y-1.5"><Label>Company reg no.</Label><Input value={form.company_reg_number} onChange={(e) => setForm({ ...form, company_reg_number: e.target.value })} data-testid="insurer-form-reg" /></div>
              <div className="space-y-1.5 md:col-span-2"><Label>VAT number</Label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} data-testid="insurer-form-vat" /></div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Street</Label>
                <AddressAutocomplete
                  value={form.address_street}
                  onChange={(v: string) => setForm((f) => ({ ...f, address_street: v }))}
                  onResolve={(p: { formatted_address: string; city?: string; province?: string; postal_code?: string; country?: string }) => setForm((f) => ({
                    ...f,
                    address_street: p.formatted_address,
                    city: p.city || f.city,
                    province: p.province || f.province,
                    postal_code: p.postal_code || f.postal_code,
                    country: p.country || f.country,
                  }))}
                  placeholder="Start typing to auto-fill city, province, postal code and country…"
                  testid="insurer-form-street"
                />
              </div>
              <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="insurer-form-city" /></div>
              <div className="space-y-1.5"><Label>Province</Label><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="insurer-form-province" /></div>
              <div className="space-y-1.5"><Label>Postal code</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="insurer-form-postal" /></div>
              <div className="space-y-1.5"><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="insurer-form-country" /></div>
              <div className="md:col-span-2 flex justify-end"><Button type="submit" variant="gradient" disabled={busy}><Plus size={14} /> Create insurer</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>All insurers · {rows.length}</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-auto">
          <table className="w-full text-sm font-body">
            <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
              <tr>
                <th className="py-2 px-3 text-left">ID</th>
                <th className="py-2 px-3 text-left">Trading name</th>
                <th className="py-2 px-3 text-left">FSP</th>
                <th className="py-2 px-3 text-left">City</th>
                <th className="py-2 px-3 text-left">Contact</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-raptor-navy/50">No insurers on the platform yet. Click "New insurer" to add one.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/60" data-testid={`insurer-row-${r.id}`}>
                  <td className="py-2 px-3 font-tabular text-xs">{r.id}</td>
                  <td className="py-2 px-3"><b>{r.name}</b></td>
                  <td className="py-2 px-3 font-tabular text-xs">{r.fsp_number || '—'}</td>
                  <td className="py-2 px-3">{r.city || '—'}</td>
                  <td className="py-2 px-3 text-xs">{r.contact_email || '—'}</td>
                  <td className="py-2 px-3">{r.active ? <Badge variant="success">Active</Badge> : <Badge variant="muted">Inactive</Badge>}</td>
                  <td className="py-2 px-3 flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEdit(r)} data-testid={`edit-${r.id}`}>Edit</Button>
                    <Button size="sm" variant="gradient" onClick={() => doSwitch(r.id)} data-testid={`switch-${r.id}`}><ArrowLeftRight size={14} /> Switch in</Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setDel(r); setDelConfirm(''); }} data-testid={`delete-${r.id}`}><Trash2 size={14} /> Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {edit && (
        <div className="fixed inset-0 bg-raptor-navy/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-md max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-border/60">
              <div className="font-headline text-xl text-raptor-navy flex items-center gap-2"><Building2 size={18} /> Edit insurer</div>
              <div className="text-xs text-raptor-navy/60 font-body">{edit.id}</div>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1.5"><Label>Trading name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>FSP number</Label><Input value={edit.fsp_number} onChange={(e) => setEdit({ ...edit, fsp_number: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Contact email</Label><Input type="email" value={edit.contact_email} onChange={(e) => setEdit({ ...edit, contact_email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Active</label>
            </div>
            <div className="p-4 border-t border-border/60 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
              <Button variant="gradient" onClick={saveEdit} disabled={busy}><Save size={14} /> Save changes</Button>
            </div>
          </div>
        </div>
      )}
      {del && (
        <div className="fixed inset-0 bg-raptor-navy/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setDel(null); setDelConfirm(''); }}>
          <div className="bg-white rounded-md max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-insurer-dialog">
            <div className="p-4 border-b border-border/60">
              <div className="font-headline text-xl text-red-600 flex items-center gap-2"><AlertTriangle size={18} /> Delete insurer</div>
              <div className="text-xs text-raptor-navy/60 font-body">{del.id} · {del.name}</div>
            </div>
            <div className="p-4 space-y-3">
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                This permanently deletes <b>{del.name}</b> and <b>all</b> of its data — brokers, staff, clients, sites,
                surveys, risk ratings, templates, covers, users and audit history. This <b>cannot be undone</b>.
              </div>
              <div className="space-y-1.5">
                <Label>Type <span className="font-tabular font-bold">DELETE</span> to confirm</Label>
                <Input autoFocus value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder="DELETE" data-testid="delete-confirm-input" />
              </div>
            </div>
            <div className="p-4 border-t border-border/60 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setDel(null); setDelConfirm(''); }}>Cancel</Button>
              <Button variant="destructive" onClick={doDelete} disabled={busy || delConfirm !== 'DELETE'} data-testid="delete-insurer-confirm-btn"><Trash2 size={14} /> Delete insurer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
